import { request } from "../utils/request";
import { toastr } from "react-redux-toastr";
import { saveAs } from "file-saver";
import { isAxiosError } from "axios";

/**
 * @typedef GetListForInvoicingPayload
 * @property {Date} dateFrom
 * @property {Date} dateTo
 * @property {number[]} clientIds
 *
 * @param {GetListForInvoicingPayload} param
 */
export async function getDeliveriesForInvoicing({
  dateFrom,
  dateTo,
  clientIds = [],
}) {
  const response = await request.get(`/api/invoices/for-invoicing`, {
    params: {
      from: dateFrom,
      to: dateTo,
      clientIds
    }
  });

  return response.data;
}

export async function getDeliveriesForInvoicingAsCsv({
  dateFrom,
  dateTo,
  clientIds = [],
}) {
  const response = await request.get('/api/invoices/for-invoicing/csv-report', {
    params: {
      from: dateFrom,
      to: dateTo,
      clientIds
    },
    responseType: "blob"
  });

  saveAs(response.data, "invoices.csv");
}

/**
 * @typedef CreateInvoicePayload
 * @property {string} invoiceDate
 * @property {string} from
 * @property {string} to
 * @property {number[]} locationIds
 *
 * @param {CreateInvoicePayload} param
 */
export async function createInvoice({ invoiceDate, from, to, locationIds = [] }) {
  await request.post(`/api/invoices`, {
    invoiceDate,
    from,
    to,
    locationIds,
  });
}

/**
 * @typedef GetInvoicesPayload
 * @property {string} clientName
 * @property {string} from
 * @property {string} to
 * @property {"READY" | "EXPORTED" | "ERROR" | "EXPORTING" | ""} status
 *
 * @param {GetInvoicesPayload} param
 */
export async function getInvoices({
  clientName = "",
  from = "",
  to = "",
  status = "",
}) {
  try {
    const res = await request.get(`/api/invoices`, {
      params: {
        clientName,
        from,
        to,
        status
      }
    });
    if (res.status !== 200) {
      throw res;
    }
    return res.data;
  } catch (error) {
    toastr.error(`Retrieval Error: ${error}`);
    if(isAxiosError(error)){
      return error.response.data;
    }
  }
}

export async function getInvoiesAsCsv({
  clientName = "",
  from = "",
  to = "",
  status = "",
}) {
  const response = await request.get('/api/invoices/csv-report', {
    params: {
      clientName,
      from,
      to,
      status
    },
    responseType: "blob"
  });

  saveAs(response.data, "invoices.csv");
}

export async function getInvoiceDetails(id = "") {
  const response = await request.get(`/api/invoices/${id}`);
  return response.data;
}

export async function deleteInvoice(id = "") {
  await request.delete(`/api/invoices/${id}`);
}

export async function cancelInvoice(id = "") {
  await request.put(`/api/invoices/${id}/cancel`);
}

/**
 *
 * @param {number[]} invoiceIds
 */
export async function exportInvoices(invoiceIds = []) {
  try {
    const res = await request.post(`/api/invoices/export`, { invoiceIds });
    if(res.status !== 200){
      throw res;
    }
    return res;
  } catch (error) {
    if(isAxiosError(error)){
      toastr.error(`Retrieval Error: ${error.response.data}`);
      return error.response.data;
    }
  }
}

export async function getStatuses() {
  try {
    const res = await request.get(`/api/invoices/lookups/statuses`);
    if (res.status !== 200) {
      throw res;
    }
    return res.data;
  } catch (error) {
    if(isAxiosError(error)){
      toastr.error(`Could not retrieve status list. Error: ${error.response.data}`);
    }
  }
}

export async function downloadExportedInvoice(id) {
  const response = await request.get(`/api/invoices/${id}/download`, {
    responseType: "blob"
  });

  saveAs(response.data, "invoice.pdf");
}